// Utility functions for localStorage operations

export const getAllItems = (entity) => {
  try {
    const items = localStorage.getItem(entity);
    return items ? JSON.parse(items) : [];
  } catch (error) {
    console.error(`Error getting items from ${entity}:`, error);
    return [];
  }
};

export const getItemById = (entity, id) => {
  const items = getAllItems(entity);
  return items.find((item) => item.id === id);
};

export const addItem = (entity, item) => {
  const items = getAllItems(entity);
  
  // Generate ID if not provided
  if (!item.id) {
    item.id = Date.now().toString();
  }
  
  // Add creation timestamp for real-time tracking
  if (!item.createdAt) {
    item.createdAt = new Date().toISOString();
  }
  
  items.push(item);
  localStorage.setItem(entity, JSON.stringify(items));
  return item;
};

export const updateItem = (entity, updatedItem) => {
  const items = getAllItems(entity);
  const index = items.findIndex((item) => item.id === updatedItem.id);
  if (index !== -1) {
    items[index] = { ...items[index], ...updatedItem };
    localStorage.setItem(entity, JSON.stringify(items));
    return items[index];
  }
  return null;
};

export const deleteItem = (entity, id) => {
  const items = getAllItems(entity).filter((i) => i.id !== id);
  localStorage.setItem(entity, JSON.stringify(items));
  return true;
};

// ===== BATCH-SPECIFIC HELPER FUNCTIONS =====

export const createBatch = (batchData) => {
  // Generate batch ID with readable format
  const courseShort = batchData.courseName?.substring(0, 3).toUpperCase() || 'BTH';
  const dayShort = batchData.days?.[0]?.substring(0, 3).toUpperCase() || 'UNK';
  const timeShort = batchData.startTime?.replace(':', '') || 'TIME';
  const batchId = `${courseShort}-${dayShort}-${timeShort}-${Date.now().toString().slice(-4)}`;
  
  const batch = {
    id: batchId,
    courseId: batchData.courseId,
    courseName: batchData.courseName, // For easy reference
    batchName: batchData.batchName || `${batchData.courseName} - ${batchData.days?.join('/')} ${batchData.startTime}`,
    days: batchData.days || [], // ["Monday", "Wednesday", "Friday"]
    startTime: batchData.startTime, // "18:00"
    endTime: batchData.endTime, // "20:00"
    startDate: batchData.startDate, // Course start date
    endDate: batchData.endDate, // Course end date
    trainerId: batchData.trainerId,
    trainerName: batchData.trainerName, // For easy reference
    room: batchData.room || 'TBD',
    maxCapacity: parseInt(batchData.maxCapacity) || 15,
    currentEnrollment: 0, // Will be calculated dynamically
    status: 'active', // active, completed, cancelled
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  };
  
  return addItem('batches', batch);
};

export const getBatchesByCourse = (courseId) => {
  const batches = getAllItems('batches');
  return batches.filter(batch => batch.courseId === courseId);
};

export const getBatchWithEnrollment = (batchId) => {
  const batch = getItemById('batches', batchId);
  if (!batch) return null;
  
  // Calculate current enrollment
  const enrollments = getAllItems('enrollments') || [];
  const currentEnrollment = enrollments.filter(e => 
    e.batchId === batchId && e.status === 'active'
  ).length;
  
  return {
    ...batch,
    currentEnrollment,
    availableSpots: batch.maxCapacity - currentEnrollment,
    isFull: currentEnrollment >= batch.maxCapacity
  };
};

export const getAllBatchesWithEnrollment = () => {
  const batches = getAllItems('batches');
  return batches.map(batch => getBatchWithEnrollment(batch.id));
};

export const checkTrainerConflict = (trainerId, days, startTime, endTime, startDate, endDate, excludeBatchId = null) => {
  const batches = getAllItems('batches');
  
  return batches.filter(batch => {
    if (excludeBatchId && batch.id === excludeBatchId) return false;
    if (batch.trainerId !== trainerId) return false;
    if (batch.status !== 'active') return false;
    
    // Check if any days overlap
    const hasDayOverlap = batch.days.some(day => days.includes(day));
    if (!hasDayOverlap) return false;
    
    // Check if times overlap
    const batchStart = batch.startTime;
    const batchEnd = batch.endTime;
    
    const hasTimeOverlap = (
      (startTime >= batchStart && startTime < batchEnd) ||
      (endTime > batchStart && endTime <= batchEnd) ||
      (startTime <= batchStart && endTime >= batchEnd)
    );
    
    if (!hasTimeOverlap) return false;
    
    // NEW: Check if date periods overlap
    const newStartDate = new Date(startDate);
    const newEndDate = new Date(endDate);
    const batchStartDate = new Date(batch.startDate);
    const batchEndDate = new Date(batch.endDate);
    
    // Two periods overlap if one starts before the other ends AND vice versa
    const hasDatePeriodOverlap = (
      newStartDate < batchEndDate && batchStartDate < newEndDate
    );
    
    // Only report conflict if periods actually overlap
    return hasDatePeriodOverlap;
  });
};

// ===== ENROLLMENT HELPER FUNCTIONS =====

export const createEnrollment = (studentId, batchId) => {
  const enrollment = {
    id: `enroll-${studentId}-${batchId}-${Date.now()}`,
    studentId,
    batchId,
    enrollmentDate: new Date().toISOString(),
    status: 'active', // active, completed, dropped, transferred
    paymentStatus: 'pending', // pending, partial, paid, overdue
    createdAt: new Date().toISOString()
  };
  
  return addItem('enrollments', enrollment);
};

export const getStudentEnrollments = (studentId) => {
  const enrollments = getAllItems('enrollments');
  return enrollments.filter(e => e.studentId === studentId && e.status === 'active');
};

export const getBatchEnrollments = (batchId) => {
  const enrollments = getAllItems('enrollments');
  return enrollments.filter(e => e.batchId === batchId && e.status === 'active');
};

// ===== ATTENDANCE HELPER FUNCTIONS =====

export const createAttendanceRecord = (batchId, sessionDate, attendanceData) => {
  const record = {
    id: `attend-${batchId}-${sessionDate}-${Date.now()}`,
    batchId,
    sessionDate, // "2025-10-01"
    attendances: attendanceData, // [{studentId: "123", attended: true, notes: ""}]
    createdAt: new Date().toISOString(),
    recordedBy: 'admin' // Could be trainer ID in future
  };
  
  return addItem('attendance', record);
};

export const getAttendanceByBatch = (batchId) => {
  const attendance = getAllItems('attendance');
  return attendance.filter(record => record.batchId === batchId);
};

export const getStudentAttendanceRate = (studentId, batchId) => {
  const attendance = getAllItems('attendance');
  const batchAttendance = attendance.filter(record => record.batchId === batchId);
  
  let totalSessions = 0;
  let attendedSessions = 0;
  
  batchAttendance.forEach(record => {
    const studentRecord = record.attendances.find(a => a.studentId === studentId);
    if (studentRecord) {
      totalSessions++;
      if (studentRecord.attended) attendedSessions++;
    }
  });
  
  return totalSessions > 0 ? (attendedSessions / totalSessions) * 100 : 0;
};

// NEW: Get attendance records by date range
export const getAttendanceByDateRange = (startDate, endDate) => {
  const attendanceRecords = getAllItems('attendance');
  const start = new Date(startDate);
  const end = new Date(endDate);
  
  return attendanceRecords.filter(record => {
    const recordDate = new Date(record.sessionDate);
    return recordDate >= start && recordDate <= end;
  });
};

// NEW: Get overall attendance statistics for a batch
export const getBatchAttendanceStats = (batchId) => {
  const attendanceRecords = getAttendanceByBatch(batchId);
  const enrollments = getBatchEnrollments(batchId);
  
  if (attendanceRecords.length === 0) {
    return {
      totalSessions: 0,
      totalStudents: enrollments.length,
      averageAttendanceRate: 0,
      bestAttendanceRate: 0,
      worstAttendanceRate: 0,
      sessionStats: []
    };
  }
  
  // Calculate per-session statistics
  const sessionStats = attendanceRecords.map(record => {
    const presentCount = record.attendances.filter(a => a.attended).length;
    const totalCount = record.attendances.length;
    const attendanceRate = totalCount > 0 ? (presentCount / totalCount) * 100 : 0;
    
    return {
      sessionDate: record.sessionDate,
      presentCount,
      totalCount,
      attendanceRate
    };
  });
  
  // Calculate individual student stats
  const studentStats = enrollments.map(enrollment => {
    const studentId = enrollment.studentId;
    const attendanceRate = getStudentAttendanceRate(studentId, batchId);
    return {
      studentId,
      attendanceRate
    };
  });
  
  const averageAttendanceRate = sessionStats.length > 0 
    ? sessionStats.reduce((sum, s) => sum + s.attendanceRate, 0) / sessionStats.length 
    : 0;
  
  const bestAttendanceRate = studentStats.length > 0 
    ? Math.max(...studentStats.map(s => s.attendanceRate))
    : 0;
  
  const worstAttendanceRate = studentStats.length > 0 
    ? Math.min(...studentStats.map(s => s.attendanceRate))
    : 0;
  
  return {
    totalSessions: attendanceRecords.length,
    totalStudents: enrollments.length,
    averageAttendanceRate,
    bestAttendanceRate,
    worstAttendanceRate,
    sessionStats,
    studentStats
  };
};

// NEW: Get attendance summary for all active batches
export const getOverallAttendanceStats = () => {
  const batches = getAllItems('batches').filter(b => b.status === 'active');
  
  const batchStats = batches.map(batch => {
    const stats = getBatchAttendanceStats(batch.id);
    return {
      batchId: batch.id,
      batchName: batch.batchName,
      courseName: batch.courseName,
      ...stats
    };
  });
  
  const totalSessions = batchStats.reduce((sum, b) => sum + b.totalSessions, 0);
  const totalStudents = batchStats.reduce((sum, b) => sum + b.totalStudents, 0);
  const overallAverageRate = batchStats.length > 0 
    ? batchStats.reduce((sum, b) => sum + b.averageAttendanceRate, 0) / batchStats.length
    : 0;
  
  return {
    totalBatches: batches.length,
    totalSessions,
    totalStudents,
    overallAverageRate,
    batchStats
  };
};

export const initializeMockData = () => {
  console.log('🚫 No mock data initialization - using only real-time data from application usage');
  
  // The Benefits page will only display teachers, payments, courses, and sessions
  // that have been actually created through the application interface
  
  // No mock data is created - everything must be added through:
  // - /trainers page for teachers
  // - /courses page for courses  
  // - /students page for students
  // - /payments page for payments
  // - /sessions page for sessions
  // - /batches (NEW) for course batches
  // - /enrollments (NEW) for student-batch relationships
  // - /attendance (NEW) for session attendance
};

export default {
  getAllItems,
  getItemById,
  addItem,
  updateItem,
  deleteItem,
  initializeMockData,
  // Batch functions
  createBatch,
  getBatchesByCourse,
  getBatchWithEnrollment,
  getAllBatchesWithEnrollment,
  checkTrainerConflict,
  // Enrollment functions
  createEnrollment,
  getStudentEnrollments,
  getBatchEnrollments,
  // Attendance functions
  createAttendanceRecord,
  getAttendanceByBatch,
  getStudentAttendanceRate,
  getAttendanceByDateRange,
  getBatchAttendanceStats,
  getOverallAttendanceStats,
};